import type { ExerciseDef } from './types'

// ─── 3.5 Tensors & broadcasting ───────────────────────────────────────────────

export const tensorIntro: ExerciseDef = {
  starter: `// The course's Tensor holds a grid of numbers (rows × cols) — and, as you'll see in
// Module 7, it can also compute gradients. For now, treat it as a smart matrix.
const X = Tensor.from([
  [1, 2, 3],
  [4, 5, 6],
])
console.log("shape:", X.shape)          // [2, 3]
console.log("entry (1, 2):", X.get(1, 2))

// Inside, the numbers are stored in ONE flat list, row after row:
console.log("flat data:", Array.from(X.data))
// so entry (i, j) lives at position i * cols + j:
console.log("position 1*3 + 2 =", X.data[1 * 3 + 2])

// Broadcasting: a 1×3 row is added to EVERY row (that's how a bias works)
const b = Tensor.from([[10, 20, 30]])
console.log(X.add(b).toArray())
// A single number is added to every entry
console.log(X.mul(2).toArray())

// Shapes that don't fit are an error — the most common bug in ML code
try {
  X.add(Tensor.from([[1, 2]]))
} catch (e) {
  console.warn(String(e))
}
`,
}

export const flatIndex: ExerciseDef = {
  starter: `// 1. flatIndex(i, j, cols): where entry (i, j) of a rows×cols matrix sits in the flat list.
function flatIndex(i: number, j: number, cols: number): number {
  return 0
}

// 2. reshape(flat, rows, cols): cut a flat list into rows of length cols.
//    reshape([1, 2, 3, 4, 5, 6], 2, 3) → [[1, 2, 3], [4, 5, 6]]
//    If rows × cols doesn't equal flat.length, throw an Error.
function reshape(flat: number[], rows: number, cols: number): number[][] {
  return []
}

// 3. flatten(M): the opposite — glue the rows back into one list.
function flatten(M: number[][]): number[] {
  return []
}

console.log(flatIndex(1, 2, 3), reshape([1, 2, 3, 4, 5, 6], 3, 2), flatten([[1, 2], [3, 4]]))
`,
  solution: `function flatIndex(i: number, j: number, cols: number): number {
  return i * cols + j
}

function reshape(flat: number[], rows: number, cols: number): number[][] {
  if (rows * cols !== flat.length) throw new Error(\`can't reshape \${flat.length} numbers into \${rows}×\${cols}\`)
  const out: number[][] = []
  for (let i = 0; i < rows; i++) out.push(flat.slice(i * cols, (i + 1) * cols))
  return out
}

function flatten(M: number[][]): number[] {
  const out: number[] = []
  for (const row of M) out.push(...row)
  return out
}

console.log(flatIndex(1, 2, 3), reshape([1, 2, 3, 4, 5, 6], 3, 2), flatten([[1, 2], [3, 4]]))
`,
  tests: `
test("flatIndex(1, 2, 3) is 5", () => expect(flatIndex(1, 2, 3), "flatIndex(1, 2, 3)").toBe(5))
test("flatIndex(0, 0, 7) is 0", () => expect(flatIndex(0, 0, 7), "flatIndex(0, 0, 7)").toBe(0))
test("reshape into 2 × 3", () => expect(reshape([1, 2, 3, 4, 5, 6], 2, 3), "reshape(…, 2, 3)").toEqual([[1, 2, 3], [4, 5, 6]]))
test("reshape into 3 × 2", () => expect(reshape([1, 2, 3, 4, 5, 6], 3, 2), "reshape(…, 3, 2)").toEqual([[1, 2], [3, 4], [5, 6]]))
test("reshape refuses sizes that don't fit", () => expect(() => reshape([1, 2, 3], 2, 2), "reshape([1, 2, 3], 2, 2)").toThrow())
test("flatten undoes reshape", () => expect(flatten(reshape(range(12), 4, 3)), "flatten(reshape(range(12), 4, 3))").toEqual(range(12)))
test("matches the Tensor's own storage", () => {
  const __t = Tensor.from([[1, 2, 3], [4, 5, 6]])
  expect(__t.data[flatIndex(1, 0, 3)], "data[flatIndex(1, 0, 3)]").toBe(4)
})
`,
}

export const axisSums: ExerciseDef = {
  starter: `// Rows are examples, columns are features:
const X = [
  [1, 10, 100],
  [2, 20, 200],
  [3, 30, 300],
  [4, 40, 400],
]

// 1. columnMeans(X): the average of each column → one number per feature.
//    For X above: [2.5, 25, 250]
function columnMeans(X: number[][]): number[] {
  return []
}

// 2. center(X): subtract each column's mean from that column (broadcasting a row!).
//    Afterwards every column averages 0.
function center(X: number[][]): number[][] {
  return X
}

// 3. standardize(X): center, then divide each column by its standard deviation
//    (the square root of the average squared value of the centered column).
//    Now every feature has mean 0 and spread 1 — so no feature dominates just because its numbers are big.
function standardize(X: number[][]): number[][] {
  return X
}

console.log(columnMeans(X), center(X), standardize(X))
`,
  solution: `const X = [
  [1, 10, 100],
  [2, 20, 200],
  [3, 30, 300],
  [4, 40, 400],
]

function columnMeans(X: number[][]): number[] {
  const cols = X[0].length
  const sums = new Array(cols).fill(0)
  for (const row of X) for (let j = 0; j < cols; j++) sums[j] += row[j]
  return sums.map(s => s / X.length)
}

function center(X: number[][]): number[][] {
  const m = columnMeans(X)
  return X.map(row => row.map((v, j) => v - m[j]))
}

function standardize(X: number[][]): number[][] {
  const C = center(X)
  const std = columnMeans(C.map(row => row.map(v => v * v))).map(Math.sqrt)
  return C.map(row => row.map((v, j) => v / std[j]))
}

console.log(columnMeans(X), center(X), standardize(X))
`,
  tests: `
const __X = [[1, 10, 100], [2, 20, 200], [3, 30, 300], [4, 40, 400]]
test("column means are [2.5, 25, 250]", () => expect(columnMeans(__X), "columnMeans(X)").toEqual([2.5, 25, 250]))
test("centered first row is [−1.5, −15, −150]", () => expect(center(__X)[0], "center(X)[0]").toEqual([-1.5, -15, -150]))
test("centered columns average 0", () => expect(columnMeans(center([[1, 5], [3, 9], [8, 1]])).every(v => Math.abs(v) < 1e-12), "column means after centering").toBe(true))
test("center doesn't change the shape", () => expect(center(__X).length, "rows").toBe(4))
test("after standardize, every column has the same values", () => {
  const __S = standardize(__X)
  expect(__S[0][0], "S[0][0]").toBeCloseTo(__S[0][2])
  expect(__S[0][0], "S[0][0]").toBeCloseTo(-1.5 / Math.sqrt(1.25))
})
test("standardized columns have spread 1", () => {
  const __S = standardize([[1, 5], [3, 9], [8, 1]])
  expect(columnMeans(__S.map(r => r.map(v => v * v)))[1], "average square of column 1").toBeCloseTo(1)
})
`,
}

// ─── 3.6 Eigenvectors & PCA ───────────────────────────────────────────────────

export const eigenCheck: ExerciseDef = {
  starter: `const A = [
  [2, 1],
  [1, 2],
]

// Most vectors get turned by A:
console.log(mat.apply(A, [1, 0]))     // [2, 1] — a different direction
// …but these only get stretched:
console.log(mat.apply(A, [1, 1]))     // [3, 3]  = 3 × [1, 1]   → eigenvalue 3
console.log(mat.apply(A, [1, -1]))    // [1, -1] = 1 × [1, −1]  → eigenvalue 1

// Power iteration: multiply by A again and again (rescaling each time).
// Any starting vector swings round to the direction A stretches MOST.
let v = [1, 0]
for (let step = 0; step < 8; step++) {
  const Av = mat.apply(A, v)
  v = vec.scale(Av, 1 / vec.norm(Av))
  console.log(step, v.map(x => round(x, 4)))
}
`,
}

export const powerIteration: ExerciseDef = {
  starter: `// 1. powerIteration(A, steps): the direction A stretches the most.
//    Start from random numbers, then repeat: v ← A·v, then divide v by its length.
function powerIteration(A: number[][], steps = 200): number[] {
  let v = A.map(() => random.normal())
  // TODO
  return v
}

// 2. eigenvalue(A, v): how much A stretches the (length-1) vector v:  v · (A v)
function eigenvalue(A: number[][], v: number[]): number {
  return 0
}

const A = [[4, 1], [2, 3]]
const v = powerIteration(A)
console.log(v, eigenvalue(A, v))   // ≈ ±[0.707, 0.707] and 5
`,
  solution: `function powerIteration(A: number[][], steps = 200): number[] {
  let v = A.map(() => random.normal())
  for (let s = 0; s < steps; s++) {
    const Av = mat.apply(A, v)
    v = vec.scale(Av, 1 / vec.norm(Av))
  }
  return v
}

function eigenvalue(A: number[][], v: number[]): number {
  return vec.dot(v, mat.apply(A, v))
}

const A = [[4, 1], [2, 3]]
const v = powerIteration(A)
console.log(v, eigenvalue(A, v))
`,
  tests: `
test("finds the top eigenvector of [[2, 1], [1, 2]]", () => {
  const __v = powerIteration([[2, 1], [1, 2]])
  expect(Math.abs(vec.dot(__v, [Math.SQRT1_2, Math.SQRT1_2])), "|v · [0.707, 0.707]|").toBeCloseTo(1, 1e-6)
})
test("the result has length 1", () => expect(vec.norm(powerIteration([[3, 0], [0, 1]])), "length").toBeCloseTo(1))
test("eigenvalue of [[2, 1], [1, 2]] along it is 3", () => {
  const __A = [[2, 1], [1, 2]]
  expect(eigenvalue(__A, powerIteration(__A)), "eigenvalue").toBeCloseTo(3, 1e-6)
})
test("works for a 3 × 3 matrix", () => {
  const __A = [[5, 0, 0], [0, 2, 0], [0, 0, 1]]
  expect(Math.abs(powerIteration(__A)[0]), "|first component|").toBeCloseTo(1, 1e-6)
  expect(eigenvalue(__A, powerIteration(__A)), "eigenvalue").toBeCloseTo(5, 1e-6)
})
`,
}

const PCA_HELPERS = `function powerIteration(A: number[][], steps = 200): number[] {
  let v = A.map(() => random.normal())
  for (let s = 0; s < steps; s++) {
    const Av = mat.apply(A, v)
    v = vec.scale(Av, 1 / vec.norm(Av))
  }
  return v
}`

export const pcaDigits: ExerciseDef = {
  starter: `// (from the last challenge)
${PCA_HELPERS}

// 1. covariance(X): a d×d matrix for n×d data. Center the columns first, then
//    C[a][b] = average over the rows of  x[a] · x[b].   (Tip: Xc transposed × Xc, divided by n.)
function covariance(X: number[][]): number[][] {
  return []
}

// 2. pca(X, k): the top k principal components — k unit vectors of length d.
//    Find the top eigenvector v of C with powerIteration, its eigenvalue λ = v·(Cv),
//    then "deflate":  C ← C − λ·v·vᵀ  (remove that direction) and repeat.
function pca(X: number[][], k: number): number[][] {
  return []
}

// 3. project(X, comps): each row's coordinates along the components (center X first!).
function project(X: number[][], comps: number[][]): number[][] {
  return []
}

// 196 pixels per digit → 2 numbers per digit. Do similar digits land near each other?
const { X, y } = data.digits(300)
const comps = pca(X, 2)
plot.scatter(project(X, comps), { labels: y, title: "300 digits, squashed from 196 numbers to 2" })
`,
  solution: `${PCA_HELPERS}

function centerCols(X: number[][]): number[][] {
  const n = X.length
  const means = X[0].map((_, j) => X.reduce((s, row) => s + row[j], 0) / n)
  return X.map(row => row.map((v, j) => v - means[j]))
}

function covariance(X: number[][]): number[][] {
  const Xc = centerCols(X)
  return mat.scale(mat.mul(mat.transpose(Xc), Xc), 1 / X.length)
}

function pca(X: number[][], k: number): number[][] {
  let C = covariance(X)
  const comps: number[][] = []
  for (let c = 0; c < k; c++) {
    const v = powerIteration(C)
    const lambda = vec.dot(v, mat.apply(C, v))
    comps.push(v)
    C = C.map((row, i) => row.map((x, j) => x - lambda * v[i] * v[j]))
  }
  return comps
}

function project(X: number[][], comps: number[][]): number[][] {
  return centerCols(X).map(row => comps.map(c => vec.dot(row, c)))
}

const { X, y } = data.digits(300)
const comps = pca(X, 2)
plot.scatter(project(X, comps), { labels: y, title: "300 digits, squashed from 196 numbers to 2" })
`,
  tests: `
test("covariance of [[1, 2], [3, 4], [5, 6]]", () => {
  const __C = covariance([[1, 2], [3, 4], [5, 6]])
  expect(__C.length, "rows").toBe(2)
  expect(__C[0][0], "C[0][0]").toBeCloseTo(8 / 3)
  expect(__C[0][1], "C[0][1]").toBeCloseTo(8 / 3)
})
test("covariance is d × d", () => expect(covariance([[1, 2, 3], [2, 2, 2], [0, 1, 5], [4, 4, 4]]).map(r => r.length), "row lengths").toEqual([3, 3, 3]))
test("the first component of a stretched cloud points along the stretch", () => {
  const __X = range(50).map(i => [i, 2 * i + (i % 3)])
  const __c = pca(__X, 1)[0]
  expect(Math.abs(__c[1] / __c[0]), "|slope| of the first component").toBeBetween(1.9, 2.1)
})
test("components have length 1 and are perpendicular", () => {
  const __X = range(40).map(i => [Math.sin(i), Math.cos(i * 1.3) * 2, i % 5])
  const [__a, __b] = pca(__X, 2)
  expect(vec.norm(__a), "|first|").toBeCloseTo(1, 1e-6)
  expect(vec.norm(__b), "|second|").toBeCloseTo(1, 1e-6)
  expect(Math.abs(vec.dot(__a, __b)), "first · second").toBeLessThan(1e-4)
})
test("project gives k numbers per row", () => {
  const __X = [[1, 2], [3, 5], [6, 2], [0, 0]]
  expect(project(__X, pca(__X, 1)).map(r => r.length), "lengths").toEqual([1, 1, 1, 1])
})
`,
}

// ─── 3.7 Project: a movie recommender ─────────────────────────────────────────

const REC_HELPERS = `function mean(xs: number[]): number {
  return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0
}

// the average of a user's ratings (ignoring 0 = "not rated")
function userMean(row: number[]): number {
  return mean(row.filter(x => x > 0))
}`

const REC_SIM_SOLUTION = `function center(row: number[]): number[] {
  const m = userMean(row)
  return row.map(x => (x > 0 ? x - m : 0))
}

function similarity(R: number[][], u: number, v: number): number {
  const a = center(R[u])
  const b = center(R[v])
  let dot = 0, na = 0, nb = 0, shared = 0
  for (let m = 0; m < a.length; m++) {
    if (R[u][m] > 0 && R[v][m] > 0) {
      dot += a[m] * b[m]
      na += a[m] * a[m]
      nb += b[m] * b[m]
      shared++
    }
  }
  if (shared < 2 || na === 0 || nb === 0) return 0
  return dot / Math.sqrt(na * nb)
}`

export const recSimilarity: ExerciseDef = {
  starter: `const { R, movies, users } = data.ratings()
console.table(R.map((row, u) => ({ user: users[u], ...Object.fromEntries(movies.map((m, i) => [m, row[i] || "·"])) })))

${REC_HELPERS}

// 1. center(row): subtract the user's own average from every movie they rated; unrated stays 0.
//    Some people rate everything 4–5, others 2–3. Centering compares tastes, not generosity.
//    center([4, 0, 2]) → [1, 0, -1]
function center(row: number[]): number[] {
  return row
}

// 2. similarity(R, u, v): cosine similarity of the two CENTERED rows — but only over the
//    movies BOTH users rated (check R, not the centered rows). Return 0 if they share
//    fewer than 2 movies, or if either part is all zeros.
function similarity(R: number[][], u: number, v: number): number {
  return 0
}

// Who has the most similar taste to Ada (user 0)?
const scores = users.map((_, v) => (v === 0 ? -Infinity : similarity(R, 0, v)))
console.log("Ada's taste twin:", users[vec.argmax(scores)])
`,
  solution: `const { R, movies, users } = data.ratings()
console.table(R.map((row, u) => ({ user: users[u], ...Object.fromEntries(movies.map((m, i) => [m, row[i] || "·"])) })))

${REC_HELPERS}

${REC_SIM_SOLUTION}

const scores = users.map((_, v) => (v === 0 ? -Infinity : similarity(R, 0, v)))
console.log("Ada's taste twin:", users[vec.argmax(scores)])
`,
  tests: `
test("center([4, 0, 2]) is [1, 0, −1]", () => expect(center([4, 0, 2]), "center([4, 0, 2])").toEqual([1, 0, -1]))
test("unrated movies stay 0", () => expect(center([5, 0, 0, 3])[1], "center([5, 0, 0, 3])[1]").toBe(0))
test("same tastes, different generosity → similarity 1", () => expect(similarity([[5, 3, 4, 0], [4, 2, 3, 0]], 0, 1), "similarity").toBeCloseTo(1))
test("opposite tastes → similarity −1", () => expect(similarity([[5, 1, 3], [1, 5, 3]], 0, 1), "similarity").toBeCloseTo(-1))
test("only shared movies count", () => expect(similarity([[5, 1, 0, 3], [5, 1, 3, 0]], 0, 1), "similarity").toBeCloseTo(1))
test("fewer than 2 shared movies → 0", () => expect(similarity([[5, 0, 1], [0, 4, 2]], 0, 1), "similarity").toBe(0))
test("similarity is symmetric on the real data", () => {
  const { R: __R } = data.ratings()
  expect(similarity(__R, 3, 7), "sim(3, 7)").toBeCloseTo(similarity(__R, 7, 3))
})
`,
}

export const recPredict: ExerciseDef = {
  starter: `const { R, movies, users } = data.ratings()

${REC_HELPERS}

// (from Part 1)
${REC_SIM_SOLUTION}

function movieMean(R: number[][], m: number): number {
  return mean(R.map(row => row[m]).filter(x => x > 0))
}

// 1. predict(R, u, m): guess user u's rating of movie m.
//    Average the ratings of the OTHER users who rated m, weighted by their similarity to u.
//    Ignore users with similarity ≤ 0. If nobody's left, fall back to movieMean(R, m).
function predict(R: number[][], u: number, m: number): number {
  return movieMean(R, m)
}

// 2. recommend(R, u, k): the names of the k movies user u HASN'T rated with the highest predictions.
function recommend(R: number[][], u: number, k: number): string[] {
  return []
}

// How good is it? Hide each known rating in turn, predict it, and measure the error.
function looError(R: number[][], guess: (R: number[][], u: number, m: number) => number): number {
  let total = 0, count = 0
  for (let u = 0; u < R.length; u++) {
    for (let m = 0; m < R[u].length; m++) {
      if (R[u][m] === 0) continue
      const hidden = R.map(row => row.slice())
      hidden[u][m] = 0
      total += (guess(hidden, u, m) - R[u][m]) ** 2
      count++
    }
  }
  return Math.sqrt(total / count)   // "root mean squared error", in stars
}

console.log("baseline (movie average):", round(looError(R, (R, u, m) => movieMean(R, m)), 3))
console.log("your recommender:        ", round(looError(R, predict), 3))
console.log("for Ada:", recommend(R, 0, 2))
`,
  solution: `const { R, movies, users } = data.ratings()

${REC_HELPERS}

${REC_SIM_SOLUTION}

function movieMean(R: number[][], m: number): number {
  return mean(R.map(row => row[m]).filter(x => x > 0))
}

function predict(R: number[][], u: number, m: number): number {
  let num = 0
  let den = 0
  for (let v = 0; v < R.length; v++) {
    if (v === u || R[v][m] === 0) continue
    const s = similarity(R, u, v)
    if (s <= 0) continue
    num += s * R[v][m]
    den += s
  }
  return den > 0 ? num / den : movieMean(R, m)
}

function recommend(R: number[][], u: number, k: number): string[] {
  return range(R[u].length)
    .filter(m => R[u][m] === 0)
    .sort((a, b) => predict(R, u, b) - predict(R, u, a))
    .slice(0, k)
    .map(m => movies[m])
}

function looError(R: number[][], guess: (R: number[][], u: number, m: number) => number): number {
  let total = 0, count = 0
  for (let u = 0; u < R.length; u++) {
    for (let m = 0; m < R[u].length; m++) {
      if (R[u][m] === 0) continue
      const hidden = R.map(row => row.slice())
      hidden[u][m] = 0
      total += (guess(hidden, u, m) - R[u][m]) ** 2
      count++
    }
  }
  return Math.sqrt(total / count)
}

console.log("baseline (movie average):", round(looError(R, (R, u, m) => movieMean(R, m)), 3))
console.log("your recommender:        ", round(looError(R, predict), 3))
console.log("for Ada:", recommend(R, 0, 2))
`,
  tests: `
test("a twin's rating is copied", () => expect(predict([[5, 1, 4, 0], [5, 1, 4, 2], [1, 5, 2, 5]], 0, 3), "predict(…, 0, 3)").toBeCloseTo(2))
test("falls back to the movie average when nobody similar rated it", () => expect(predict([[5, 1, 0], [1, 5, 3], [2, 4, 5]], 0, 2), "predict").toBeCloseTo(4))
test("beats the movie-average baseline on the real data", () => {
  const { R: __R } = data.ratings()
  const __mine = looError(__R, predict)
  const __base = looError(__R, (R, u, m) => movieMean(R, m))
  expect(__mine, "your error (stars)").toBeLessThan(__base - 0.2)
})
test("recommend gives k movies the user hasn't rated", () => {
  const { R: __R, movies: __mv } = data.ratings()
  const __recs = recommend(__R, 0, 2)
  expect(__recs, "recommend(R, 0, 2)").toHaveLength(2)
  expect(__recs.every(name => __R[0][__mv.indexOf(name)] === 0), "all unrated by Ada").toBe(true)
})
test("recommendations are best-first", () => {
  const { R: __R, movies: __mv } = data.ratings()
  const __recs = recommend(__R, 2, 3).map(name => __mv.indexOf(name))
  expect(predict(__R, 2, __recs[0]) >= predict(__R, 2, __recs[1]), "first ≥ second").toBe(true)
})
`,
}

export const recMap: ExerciseDef = {
  starter: `const { R, movies } = data.ratings()

// (from the PCA lesson)
${PCA_HELPERS}

function covariance(X: number[][]): number[][] {
  const n = X.length
  const means = X[0].map((_, j) => X.reduce((s, row) => s + row[j], 0) / n)
  const Xc = X.map(row => row.map((v, j) => v - means[j]))
  return mat.scale(mat.mul(mat.transpose(Xc), Xc), 1 / n)
}

function pca(X: number[][], k: number): number[][] {
  let C = covariance(X)
  const comps: number[][] = []
  for (let c = 0; c < k; c++) {
    const v = powerIteration(C)
    const lambda = vec.dot(v, mat.apply(C, v))
    comps.push(v)
    C = C.map((row, i) => row.map((x, j) => x - lambda * v[i] * v[j]))
  }
  return comps
}

// 1. fillBlanks(R): a copy of R where every 0 (unrated) is replaced by that MOVIE's average rating.
function fillBlanks(R: number[][]): number[][] {
  return R
}

// 2. movieCoords(R): a 2-D position for every movie.
//    Run pca on the filled ratings (rows = users, columns = movies) with k = 2.
//    Each component has one number per movie: movie i sits at [comps[0][i], comps[1][i]].
function movieCoords(R: number[][]): number[][] {
  return movies.map(() => [0, 0])
}

const xy = movieCoords(R)
console.table(movies.map((m, i) => ({ movie: m, x: round(xy[i][0], 2), y: round(xy[i][1], 2) })))
plot.scatter(xy, { labels: range(movies.length), title: "movies placed by who likes them (colour = movie, see the table)" })
`,
  solution: `const { R, movies } = data.ratings()

${PCA_HELPERS}

function covariance(X: number[][]): number[][] {
  const n = X.length
  const means = X[0].map((_, j) => X.reduce((s, row) => s + row[j], 0) / n)
  const Xc = X.map(row => row.map((v, j) => v - means[j]))
  return mat.scale(mat.mul(mat.transpose(Xc), Xc), 1 / n)
}

function pca(X: number[][], k: number): number[][] {
  let C = covariance(X)
  const comps: number[][] = []
  for (let c = 0; c < k; c++) {
    const v = powerIteration(C)
    const lambda = vec.dot(v, mat.apply(C, v))
    comps.push(v)
    C = C.map((row, i) => row.map((x, j) => x - lambda * v[i] * v[j]))
  }
  return comps
}

function fillBlanks(R: number[][]): number[][] {
  const means = R[0].map((_, m) => {
    const rated = R.map(row => row[m]).filter(x => x > 0)
    return rated.reduce((a, b) => a + b, 0) / rated.length
  })
  return R.map(row => row.map((x, m) => (x > 0 ? x : means[m])))
}

function movieCoords(R: number[][]): number[][] {
  const comps = pca(fillBlanks(R), 2)
  return movies.map((_, i) => [comps[0][i], comps[1][i]])
}

const xy = movieCoords(R)
console.table(movies.map((m, i) => ({ movie: m, x: round(xy[i][0], 2), y: round(xy[i][1], 2) })))
plot.scatter(xy, { labels: range(movies.length), title: "movies placed by who likes them (colour = movie, see the table)" })
`,
  tests: `
test("fillBlanks replaces 0 with the movie's average", () => expect(fillBlanks([[4, 0], [2, 5], [0, 3]]), "fillBlanks").toEqual([[4, 4], [2, 5], [3, 3]]))
test("fillBlanks doesn't change the original", () => {
  const __R = [[1, 0], [3, 3]]
  fillBlanks(__R)
  expect(__R[0][1], "R[0][1] afterwards").toBe(0)
})
test("one position per movie", () => {
  const { R: __R } = data.ratings()
  expect(movieCoords(__R).map(p => p.length), "lengths").toEqual(new Array(10).fill(2))
})
test("the two romances land near each other", () => {
  const { R: __R, movies: __mv } = data.ratings()
  const __xy = movieCoords(__R)
  const __d = (a, b) => Math.hypot(__xy[__mv.indexOf(a)][0] - __xy[__mv.indexOf(b)][0], __xy[__mv.indexOf(a)][1] - __xy[__mv.indexOf(b)][1])
  expect(__d("Love in Lisbon", "Letters to June") < __d("Love in Lisbon", "Red Canyon Run"), "romance closer to romance than to action").toBe(true)
  expect(__d("Iron Harbour", "Red Canyon Run") < __d("Iron Harbour", "Quiet Hearts"), "action closer to action than to romance").toBe(true)
})
`,
}
